import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import '../styles/FAQ.css';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Footer from '../components/Footer'; // Assuming shared footer

gsap.registerPlugin(ScrollTrigger);

const FAQ = () => {
  const faqRefs = useRef([]);
  const [openIndex, setOpenIndex] = useState(null);

  // FAQ data
  const faqs = [
    {
      question: 'What types of industrial surplus do you buy?',
      answer: 'We purchase a wide range of industrial equipment, including transformers, switchgear, circuit breakers, pipes, valves, fittings (PVF), and other electrical components. If you have surplus equipment, contact us for a quote!'
    },
    {
      question: 'How does the process of selling surplus equipment work?',
      answer: 'Simply submit details about your equipment through our online form or call us at (800) 885-2369. We’ll provide a fast, fair quote based on market value. If accepted, we handle removal, logistics, and payment promptly.'
    },
    {
      question: 'Do you offer pickup services for surplus equipment?',
      answer: 'Yes, we manage the entire removal process, ensuring safe and efficient pickup from your warehouse or yard. Our team coordinates logistics to minimize disruption to your operations.'
    },
    {
      question: 'How do you ensure fair pricing for my equipment?',
      answer: 'Our team uses extensive market knowledge and a vast dealer network to assess your equipment’s value accurately. We provide transparent quotes to maximize your return.'
    },
    {
      question: 'What happens to the equipment after you buy it?',
      answer: 'We refurbish and redistribute usable equipment through our global network, keeping materials in circulation. Non-usable items are responsibly recycled, aligning with our sustainability focus.'
    },
    {
      question: 'Can I sell equipment that’s damaged or outdated?',
      answer: 'Yes, we buy equipment in various conditions, including damaged or obsolete items. We’ll assess its value and provide a quote based on its potential for refurbishment or recycling.'
    }
  ];


  /* ------------------------------------------------------------------
     FAQ item animations - Reveal on scroll
     ------------------------------------------------------------------ */
  useEffect(() => {
    faqRefs.current.forEach((item) => {
      if (item) {
        ScrollTrigger.create({
          trigger: item,
          start: 'top 80%',
          onEnter: () => item.classList.add('visible'),
          once: true
        });
      }
    });

    // Force refresh to handle initial states immediately
    ScrollTrigger.refresh();
  }, []);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="lenis-content">
      <title>Surplus Selling FAQ — Quotes, Pickup, Payment</title>
      <meta name="description" content="Answers on quotes, scheduling, documentation, and payment timelines when selling surplus equipment to S&D." />
      <link rel="canonical" href="https://sdindustrialsurplus.com/faq" />
      <script type="application/ld+json">{JSON.stringify({
        "@context":"https://schema.org","@type":"FAQPage","mainEntity":[
          {"@type":"Question","name":"How fast do you pay?","acceptedAnswer":{"@type":"Answer","text":"Typically within 24–48 hours after pickup and verification."}},
          {"@type":"Question","name":"Do you handle pickup?","acceptedAnswer":{"@type":"Answer","text":"Yes—insured, scheduled pickup with minimal downtime."}}
        ]
      })}</script>

      {/* Hero Banner */}
      <section className="faq-hero">
        <h1 className="faq-hero__title">
          Answers to Your <span>Questions</span> About Selling Surplus
        </h1>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <h2 className="section-heading">Frequently Asked Questions</h2>
        {faqs.map((faq, index) => (
          <div
            key={index}
            className="faq-item"
            ref={(el) => (faqRefs.current[index] = el)}
          >
            <div
              className={`faq-question ${openIndex === index ? 'active' : ''}`}
              onClick={() => toggleFAQ(index)}
            >
              <h3>{faq.question}</h3>
              <span className="toggle-icon">{openIndex === index ? '✕' : '+'}</span>
            </div>
            <div className={`faq-answer ${openIndex === index ? 'active' : ''}`}>
              <p>{faq.answer}</p>
            </div>
          </div>
        ))}
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <h2 className="section-heading section-heading--light">Still Have Questions?</h2>
        <p>
          Our team is ready to assist you with any inquiries about selling your industrial surplus equipment. Reach out today for personalized support!
        </p>
        <Link to="/contact-us">
          <button className="cta-button">Contact Us</button>
        </Link>
      </section>

      {/* Global Footer */}
      <Footer />
    </div>
  );
};

export default FAQ;